import React, { useState, useEffect, useContext, useCallback, lazy, Suspense } from 'react';
import { ThemeProvider } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { SettingsProvider, SettingsContext } from './contexts/SettingsContext';
import Characters from './components/Characters';
import SupervisorChatWindow from './components/SupervisorChatWindow';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import SettingsModal from './components/SettingsModal';
import CharacterModal from './components/CharacterModal';
import CharacterEditModal from './components/CharacterEditModal';
import LogModal from './components/LogModal';
import ProjectList from './components/ProjectList';
import ProjectCreationForm from './components/ProjectCreationForm';
import styled from 'styled-components';
import Login from './components/Login';
import ErrorBoundary from './components/ErrorBoundary';
import { auth } from './firebase';
import { onAuthStateChanged } from "firebase/auth";
import ChatWindowEnhanced from './components/ChatWindowEnhanced';
import BackgroundAnimation from './components/BackgroundAnimation';
import FileUploadModal from './components/FileUploadModal';
import UserProfileModal from './components/UserProfileModal';
import LoadingSpinner from './components/LoadingSpinner';
import { createOrUpdateUserProfile, getUserProfile } from './utils/firestore';
import conversationService from './services/conversationService';

// Lazy load Product Studio module for better performance
const ProductStudio = lazy(() => import('./modules/product-studio'));

const AppContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: rgba(249, 252, 255, 0.1);
  color: var(--text-color);
  overflow: hidden;
  font-family: var(--font-family);
  font-weight: 400;
  position: relative;
  z-index: 1;
  backdrop-filter: blur(1px);
  -webkit-backdrop-filter: blur(1px);
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  background: linear-gradient(135deg, var(--surface-color), var(--background-color));
  color: var(--text-color);
  gap: var(--space-6);
`;

const LoadingText = styled.div`
  font-size: var(--text-lg);
  font-weight: 600;
  color: var(--text-color);
  font-family: var(--font-heading-bold);
  text-align: center;
`;

const LoadingSubtext = styled.div`
  font-size: var(--text-sm);
  color: var(--text-secondary);
  text-align: center;
  max-width: 300px;
`;

const MainContent = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
  position: relative;
  z-index: 2;
  background: rgba(255, 255, 255, 0.02);
  backdrop-filter: blur(0.5px);
  -webkit-backdrop-filter: blur(0.5px);
  min-height: calc(100vh - 64px);
`;

const ChatContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding-left: ${props => props.isSidebarOpen ? '320px' : '0'};
  transition: padding-left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  height: calc(100vh - 64px);
  background: rgba(255, 255, 255, 0.01);
  position: relative;
  backdrop-filter: blur(0.5px);
  -webkit-backdrop-filter: blur(0.5px);
`;

const ContentArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: var(--space-8);
  font-family: var(--font-family);
  font-weight: 400;
  position: relative;
  z-index: 2;
  background: rgba(255, 255, 255, 0.01);
  backdrop-filter: blur(0.5px);
  -webkit-backdrop-filter: blur(0.5px);
`;

const ProductStudioModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-6);
`;

const ProductStudioContainer = styled.div`
  width: 100%;
  max-width: 1400px;
  height: 90vh;
  background: var(--surface-color);
  border-radius: var(--radius-xl);
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
`;

const ProductStudioHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--space-4) var(--space-6);
  background: rgba(255, 255, 255, 0.1);
  border-bottom: 1px solid var(--border-color-light);
`;

const ProductStudioTitle = styled.h2`
  font-size: var(--text-lg);
  font-weight: 600;
  color: var(--text-color);
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: var(--text-secondary);
  font-size: var(--text-lg);
  cursor: pointer;
  padding: var(--space-2);
  border-radius: var(--radius-lg);
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.05);
    color: var(--text-color);
  }
`;

const ProductStudioContent = styled.div`
  flex: 1;
  overflow: hidden;
`;

function AppContent({ user, userProfile }) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isLogOpen, setIsLogOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isCharacterEditOpen, setIsCharacterEditOpen] = useState(false);
  const [editingCharacter, setEditingCharacter] = useState(null);
  const [isProductStudioOpen, setIsProductStudioOpen] = useState(false);
  const { isCharacterModalOpen, closeCharacterModal, settings } = useContext(SettingsContext);
  const [logEntries, setLogEntries] = useState([]);
  const [apiLogs, setApiLogs] = useState([]);

  const { currentCharacter } = useContext(SettingsContext);

  const getCurrentSettings = () => {
    return {
      model: currentCharacter ? currentCharacter.model : settings.model,
      maxTokens: currentCharacter ? currentCharacter.maxTokens : settings.maxTokens,
      temperature: currentCharacter ? currentCharacter.temperature : settings.temperature,
      systemPrompt: currentCharacter ? currentCharacter.systemPrompt : settings.systemPrompt,
    };
  };

  useEffect(() => {
    document.body.className = isDarkMode ? 'dark-mode' : 'light-mode';
  }, [isDarkMode]);

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);
  const toggleTheme = () => setIsDarkMode(!isDarkMode);
  const openSettings = () => setIsSettingsOpen(true);
  const closeSettings = () => setIsSettingsOpen(false);
  const logout = () => {
    // Implement logout functionality
    console.log('Logout clicked');
  };
  const openLog = () => setIsLogOpen(true);
  const closeLog = () => setIsLogOpen(false);
  const openCharacterEdit = (character) => {
    setEditingCharacter(character);
    setIsCharacterEditOpen(true);
  };
  const closeCharacterEdit = () => {
    setEditingCharacter(null);
    setIsCharacterEditOpen(false);
  };

  const addLogEntry = useCallback((role, content) => {
    setLogEntries(prevEntries => [...prevEntries, { role, content }]);
  }, []);

  const addApiLog = useCallback((requestData, responseData) => {
    setApiLogs(prevLogs => [...prevLogs, { request: requestData, response: responseData }]);
  }, []);

  const [currentView, setCurrentView] = useState('chat');
  const [isProjectFormOpen, setIsProjectFormOpen] = useState(false);
  const [isFileUploadOpen, setIsFileUploadOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [selectedFolder, setSelectedFolder] = useState(null);

  const handleNavigate = (view) => {
    setCurrentView(view);
  };

  const openProjectForm = () => setIsProjectFormOpen(true);
  const closeProjectForm = () => setIsProjectFormOpen(false);

  const handleProjectCreated = (projectId) => {
    closeProjectForm();
    setCurrentView('projects');
  };

  const handleProjectSelect = (project) => {
    console.log('[App] Project selected:', project);
    setSelectedProject(project);
    setCurrentView('chat');
  };

  return (
    <AppContainer>
      <BackgroundAnimation />
      <Header
        toggleSidebar={toggleSidebar}
        isSidebarOpen={isSidebarOpen}
        openSettings={openSettings}
        toggleTheme={toggleTheme}
        isDarkMode={isDarkMode}
        openLog={openLog}
        onNavigate={handleNavigate}
          openProfile={() => setIsProfileOpen(true)}
      />
      {/* Main Content */}
      {currentView === 'chat' && (
        <MainContent>
          {console.log('[App] Rendering Sidebar with projectId:', selectedProject?.id)}
          <Sidebar
            isOpen={isSidebarOpen}
            userId={user.uid}
            projectId={selectedProject?.id}
            selectedFolder={selectedFolder}
            onFileUpload={() => setIsFileUploadOpen(true)}
            onProductStudioOpen={() => setIsProductStudioOpen(true)}
            onFolderSelect={(folder) => {
              console.log('[App] Selected folder:', folder);
              setSelectedFolder(folder);
            }}
            onConversationSelect={(conversation) => {
              console.log('[App] onConversationSelect called with conversation:', conversation);
              console.log('[App] Setting currentConversationId to:', conversation?.id || null);
              // Set the current conversation in SettingsContext
              setCurrentConversationId(conversation?.id || null);
            }}
            onCharacterEdit={openCharacterEdit}
          />
          <ChatContainer isSidebarOpen={isSidebarOpen}>
            {console.log('[App] Passing conversationId to ChatWindowEnhanced:', currentConversationId)}
            <ChatWindowEnhanced
              userId={user.uid}
              onFileUpload={() => setIsFileUploadOpen(true)}
              conversationId={currentConversationId}
            />
          </ChatContainer>
        </MainContent>
      )}
      {currentView === 'projects' && (
        <ContentArea>
          <ProjectList
            onSelectProject={handleProjectSelect}
            onCreateProject={openProjectForm}
            userId={user.uid}
          />
        </ContentArea>
      )}

      {/* Modals */}
      {isSettingsOpen && <SettingsModal isOpen={isSettingsOpen} onClose={closeSettings} />}
      {isCharacterModalOpen && <CharacterModal isOpen={isCharacterModalOpen} onClose={closeCharacterModal} />}
      {isCharacterEditOpen && (
        <CharacterEditModal
          isOpen={isCharacterEditOpen}
          onClose={closeCharacterEdit}
          character={editingCharacter}
          isNewCharacter={!editingCharacter || !editingCharacter.id}
        />
      )}
      {isLogOpen && <LogModal isOpen={isLogOpen} onClose={closeLog} entries={logEntries} apiLogs={apiLogs} />}
      {isProfileOpen && (
        <UserProfileModal
          isOpen={isProfileOpen}
          onClose={() => setIsProfileOpen(false)}
          user={user}
          userProfile={userProfile}
        />
      )}
      {isProjectFormOpen && (
        <ProjectCreationForm 
          isOpen={isProjectFormOpen} 
          onClose={closeProjectForm}
          onCreateProject={handleProjectCreated}
          userId={user.uid}
        />
      )}
      {isFileUploadOpen && (
        <FileUploadModal
          isOpen={isFileUploadOpen}
          onClose={() => setIsFileUploadOpen(false)}
          userId={user.uid}
          conversationId={currentConversationId}
          onUploadComplete={(files) => {
            console.log('Files uploaded:', files);
            // Handle successful upload - maybe show in chat or update UI
          }}
        />
      )}

      {/* Product Studio Modal */}
      {isProductStudioOpen && (
        <ProductStudioModal onClick={() => setIsProductStudioOpen(false)}>
          <ProductStudioContainer onClick={(e) => e.stopPropagation()}>
            <ProductStudioHeader>
              <ProductStudioTitle>Product Studio</ProductStudioTitle>
              <CloseButton onClick={() => setIsProductStudioOpen(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </CloseButton>
            </ProductStudioHeader>
            <ProductStudioContent>
              <Suspense fallback={
                <LoadingContainer>
                  <LoadingSpinner />
                  <LoadingText>Loading Product Studio...</LoadingText>
                </LoadingContainer>
              }>
                <ProductStudio
                  config={{
                    apiKey: localStorage.getItem('geminiApiKey') || settings?.geminiApiKey
                  }}
                  onExport={(data) => {
                    console.log('Product Studio export:', data);
                    // Handle export - could integrate with chat or save to storage
                  }}
                  onError={(error) => {
                    console.error('Product Studio error:', error);
                  }}
                />
              </Suspense>
            </ProductStudioContent>
          </ProductStudioContainer>
        </ProductStudioModal>
      )}
    </AppContainer>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      console.log("Auth state changed:", currentUser);

      if (currentUser) {
        try {
          // Check if user profile exists
          let profile = await getUserProfile(currentUser.uid);

          try {
            if (!profile) {
              // Create new user profile
              const profileData = {
                displayName: currentUser.displayName || currentUser.email?.split('@')[0] || 'User',
                email: currentUser.email,
                avatar: currentUser.photoURL || null,
                preferences: {
                  theme: 'light',
                  language: 'he',
                  notifications: true
                }
              };

              await createOrUpdateUserProfile(currentUser.uid, profileData);
              profile = await getUserProfile(currentUser.uid);
            } else {
              // Update last login
              await createOrUpdateUserProfile(currentUser.uid, {
                lastLogin: new Date()
              });
            }

            setUserProfile(profile);

            // Initialize conversation service for the user
            try {
              await conversationService.initialize(currentUser.uid);
              console.log('Conversation service initialized successfully');
            } catch (serviceError) {
              console.warn('Failed to initialize conversation service:', serviceError);
              // Service will work in offline mode
            }
          } catch (profileError) {
            console.warn('Failed to load user profile, using default profile:', profileError);
            // Create a basic profile for offline use
            const defaultProfile = {
              displayName: currentUser.displayName || currentUser.email?.split('@')[0] || 'User',
              email: currentUser.email,
              avatar: currentUser.photoURL || null,
              preferences: {
                theme: 'light',
                language: 'he',
                notifications: true
              },
              isOffline: true
            };
            setUserProfile(defaultProfile);

            // Still initialize conversation service even with default profile
            try {
              await conversationService.initialize(currentUser.uid);
              console.log('Conversation service initialized with default profile');
            } catch (serviceError) {
              console.warn('Failed to initialize conversation service with default profile:', serviceError);
            }
          }
        } catch (error) {
          console.error('Error setting up user profile:', error);
        }
      } else {
        setUserProfile(null);
      }

      setUser(currentUser);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  if (loading) {
    return (
      <LoadingContainer>
        <LoadingSpinner />
        <LoadingText>SuperChat</LoadingText>
        <LoadingSubtext>
          Loading your personalized experience...
        </LoadingSubtext>
      </LoadingContainer>
    );
  }

  console.log("User state:", user); // הוסף את זה

  return (
    <ErrorBoundary>
      <ThemeProvider theme={{ isDarkMode: false }}>
        <SettingsProvider userId={user?.uid}>
          {user ? <AppContent user={user} userProfile={userProfile} /> : <Login />}
        </SettingsProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;